import _ from 'lodash'
import matchPostalCode from '../server/utils/matchPostalCode.js'
import type {Reseller} from '../server/functions/resellers/resellers.types.js'
import type {Customer} from '../server/functions/customers/customers.types.js'

type DeliveryZone = Reseller['settings']['deliveryZones'][0]
type PositionedAddress = {type?: string, country?: string, postalCode?: string, position?: {lat?: number, lng?: number}}

export function addressInDeliveryZone(address: PositionedAddress, deliveryZone: DeliveryZone): boolean {
    // Postal code zones only contain Dutch postal codes and don't need the position of the address
    if (deliveryZone?.type === 'postalCodes') {
        return address.country === 'NL' && !!deliveryZone.postalCodes && matchPostalCode(address.postalCode as string, deliveryZone.postalCodes)
    }

    const geometries = deliveryZone.geoJSON?.geometry?.geometries || [deliveryZone.geoJSON?.geometry]

    if (!address.position) {
        return false
    }

    return _.some(geometries, (geometry) => {
        return _.every(geometry?.coordinates, (polygon, index) => {
            const inPolygon = pointInPolygon(address.position, polygon as number[][])

            // first polygon is always the outer polygon, the rest are holes
            return +index === 0 ? inPolygon : !inPolygon
        })
    })
}

function pointInPolygon(point: {lat?: number, lng?: number}, polygon: number[][]): boolean {
    // ray-casting algorithm based on
    // https://wrf.ecse.rpi.edu/Research/Short_Notes/pnpoly.html/pnpoly.html

    const x = point.lng
    const y = point.lat

    if (!x || !y) {
        return false
    }

    let inside = false

    for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
        const xi = polygon[i][0]
        const yi = polygon[i][1]
        const xj = polygon[j][0]
        const yj = polygon[j][1]

        const intersect = ((yi > y) != (yj > y)) && (x < (xj - xi) * (y - yi) / (yj - yi) + xi)
        if (intersect) {
            inside = !inside
        }
    }

    return inside
}

// The delivery zones that count for a customer: the customer's own, or all of the reseller's when the customer has none
export function getCustomerDeliveryZones(reseller: Reseller, customer?: Customer): DeliveryZone[] {
    const deliveryZones = reseller.settings.deliveryZones || []
    const customerDeliveryZoneIds = customer?.settings?.orders?.deliveryZones || []

    if (!customerDeliveryZoneIds.length) {
        return [...deliveryZones]
    }

    return customerDeliveryZoneIds.map((deliveryZoneId) => deliveryZones.find((deliveryZone) => deliveryZone.id === deliveryZoneId)).filter(Boolean)
}

export function getAddressDeliveryZoneIds(deliveryZones: DeliveryZone[], address: PositionedAddress): string[] {
    return deliveryZones.filter((deliveryZone) => addressInDeliveryZone(address, deliveryZone)).map((deliveryZone) => deliveryZone.id)
}

// The branch of the first address in one of its delivery zones, delivery addresses before pickup addresses
export function getBranchIdFromDeliveryZones(reseller: Reseller, addresses: {type?: string, deliveryZoneId?: string[]}[]): string {
    const branches = reseller.settings.branches || []

    if (!branches.length) {
        return ''
    }

    const sortedAddresses = [...addresses].sort((a, b) => Number(b.type === 'delivery') - Number(a.type === 'delivery'))

    for (const address of sortedAddresses) {
        const deliveryZoneIds = address.deliveryZoneId || []
        const branch = branches.find((branch) => branch.deliveryZoneIds?.some((deliveryZoneId) => deliveryZoneIds.includes(deliveryZoneId)))

        if (branch) {
            return branch.id
        }
    }

    return ''
}

// The branch a new order gets, like the backend derives it in orders.create: the branch of the delivery zone the order
// is in, otherwise the branch of the customer
export function deriveOrderBranchId(reseller: Reseller, customer: Customer | undefined, addresses: PositionedAddress[]): string {
    const deliveryZones = getCustomerDeliveryZones(reseller, customer)
    const zonedAddresses = addresses.map((address) => ({type: address.type, deliveryZoneId: getAddressDeliveryZoneIds(deliveryZones, address)}))

    return getBranchIdFromDeliveryZones(reseller, zonedAddresses) || customer?.branchId || ''
}
